import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import type { FacebookAuthResponse } from '@freelancer/facebook';
import type {
  BackendErrorResponse,
  BackendSuccessResponse,
} from '@freelancer/freelancer-http';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { InAppBrowser } from '@freelancer/in-app-browser';
import { Pwa } from '@freelancer/pwa';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { ErrorCodeApi } from 'api-typings/errors/errors';
import { firstValueFrom } from 'rxjs';

export enum LinkedinConnectSource {
  ONBOARDING = 'onboarding',
  SETTINGS = 'settings',
}

const linkedinCallbackUrls = {
  [LinkedinConnectSource.ONBOARDING]: '/new-freelancer',
  [LinkedinConnectSource.SETTINGS]: '/user/settings/verification?w=t',
};

@UntilDestroy({ className: 'AccountLinkingService' })
@Injectable({
  providedIn: 'root',
})
export class AccountLinkingService {
  constructor(
    private freelancerHttp: FreelancerHttp,
    private inAppBrowser: InAppBrowser,
    private pwa: Pwa,
    private router: Router,
  ) {}

  facebookLink(
    authResponse: FacebookAuthResponse,
  ): Promise<
    | BackendSuccessResponse
    | BackendErrorResponse<
        | ErrorCodeApi.USER_ALREADY_FACEBOOK_LINKED
        | ErrorCodeApi.FACEBOOK_ACCOUNT_ALREADY_IN_USE
        | 'UNKNOWN_ERROR'
      >
  > {
    return firstValueFrom(
      this.freelancerHttp
        .post<BackendSuccessResponse | BackendErrorResponse<'UNKNOWN_ERROR'>>(
          'users/0.1/users/facebook/link',
          {
            access_token: authResponse.accessToken,
            authentication_token: authResponse.authenticationToken,
            facebook_id: authResponse.userID,
          },
        )
        .pipe(untilDestroyed(this)),
    );
  }

  disconnectFacebook(): Promise<
    | BackendSuccessResponse
    | BackendErrorResponse<
        | ErrorCodeApi.FACEBOOK_ACCOUNT_NOT_CONNECTED
        | ErrorCodeApi.FACEBOOK_UNLINK_ERROR
        | 'UNKNOWN_ERROR'
      >
  > {
    return firstValueFrom(
      this.freelancerHttp
        .put<
          | BackendSuccessResponse
          | BackendErrorResponse<
              | ErrorCodeApi.FACEBOOK_ACCOUNT_NOT_CONNECTED
              | ErrorCodeApi.FACEBOOK_UNLINK_ERROR
              | 'UNKNOWN_ERROR'
            >
        >('users/0.1/self/?action=facebook_unlink', {})
        .pipe(untilDestroyed(this)),
    );
  }

  linkedinLink(source: LinkedinConnectSource): void {
    if (this.pwa.isNative()) {
      this.inAppBrowser
        .open(`/users/setup-linkedin.php?source=${source}`)
        .closeOnNavigation(linkedinCallbackUrls[source]);
    } else {
      this.router.navigate(['/users/setup-linkedin.php'], {
        queryParams: { source },
      });
    }
  }

  disconnectLinkedin(): Promise<
    BackendSuccessResponse | BackendErrorResponse<'UNKNOWN_ERROR'>
  > {
    return firstValueFrom(
      this.freelancerHttp
        .put<
          BackendSuccessResponse | BackendErrorResponse<'UNKNOWN_ERROR'>
        >('users/0.1/self/?action=linkedin_unlink', {})
        .pipe(untilDestroyed(this)),
    );
  }
}
